(() => {
  if (window.CarouselUX) return;
  window.CarouselUX = { version: '2026-08-25' };

  const recentEvents = new Map();
  const safePath = value => {
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (_) {
      return '';
    }
  };

  const emit = (event, parameters = {}) => {
    const clean = Object.fromEntries(
      Object.entries(parameters).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );

    // Google & YouTube's Shopify Web Pixel already owns these standard events.
    // Keep an audit record without pushing a second GA4-compatible dataLayer event.
    if (event === 'add_to_cart' || event === 'begin_checkout') {
      window.CarouselUX.nativeManagedEvents = window.CarouselUX.nativeManagedEvents || [];
      window.CarouselUX.nativeManagedEvents.push({ event, ...clean, owner: 'shopify_google_app' });
      window.dispatchEvent(new CustomEvent('carousel:native-analytics-event', { detail: { event, ...clean } }));
      return;
    }

    const signature = `${event}:${JSON.stringify(clean)}`;
    const now = Date.now();
    if (now - (recentEvents.get(signature) || 0) < 1000) return;
    recentEvents.set(signature, now);

    window.dataLayer = window.dataLayer || [];
    const alreadyQueued = window.dataLayer.slice(-20).some(entry =>
      entry?.event === event && entry?.carousel_source === 'theme_ux' &&
      now - Number(entry?.carousel_timestamp || 0) < 1000
    );
    if (alreadyQueued) return;

    window.dataLayer.push({
      event,
      ...clean,
      carousel_source: 'theme_ux',
      carousel_timestamp: now
    });
  };

  window.CarouselUX.emit = emit;

  const setupSizeGuides = () => {
    document.addEventListener('click', event => {
      const open = event.target.closest('[data-size-guide-open]');
      if (open) {
        const dialog = document.getElementById(open.getAttribute('aria-controls'));
        if (!dialog?.showModal) return;
        dialog._carouselTrigger = open;
        dialog.showModal();
        dialog.querySelector('[data-size-guide-close]')?.focus();
        return;
      }

      const close = event.target.closest('[data-size-guide-close]');
      if (close) close.closest('dialog')?.close();

      if (event.target.matches?.('[data-size-guide-dialog]')) {
        const rect = event.target.getBoundingClientRect();
        const inside = event.clientX >= rect.left && event.clientX <= rect.right &&
          event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) event.target.close();
      }
    });

    document.addEventListener('close', event => {
      if (!event.target.matches?.('[data-size-guide-dialog]')) return;
      event.target._carouselTrigger?.focus({ preventScroll: true });
    }, true);
  };

  const setupCartGuard = () => {
    const blockedRoot = element => element?.closest?.('[data-cart-stock-state="blocked"]');
    const announceBlocked = root => {
      const warning = root?.querySelector('[data-cart-stock-warning]');
      if (!warning) return;
      warning.setAttribute('tabindex', '-1');
      warning.focus({ preventScroll: false });
    };

    document.addEventListener('click', event => {
      const checkout = event.target.closest('[name="checkout"], [data-begin-checkout]');
      if (!checkout) return;
      const root = blockedRoot(checkout);
      if (root) {
        event.preventDefault();
        event.stopImmediatePropagation();
        announceBlocked(root);
        emit('cart_stock_error', { unavailable_items: root.querySelectorAll('[data-cart-stock-error]').length });
        return;
      }
      emit('begin_checkout');
    }, true);

    document.addEventListener('submit', event => {
      const root = blockedRoot(event.target);
      if (!root) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      announceBlocked(root);
      emit('cart_stock_error', { unavailable_items: root.querySelectorAll('[data-cart-stock-error]').length });
    }, true);

    const reportStockErrors = root => {
      const errors = root.querySelectorAll?.('[data-cart-stock-error]') || [];
      if (errors.length) emit('cart_stock_error', { unavailable_items: errors.length });
    };
    reportStockErrors(document);
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node =>
        node.nodeType === 1 && (node.matches?.('[data-cart-stock-error]') || node.querySelector?.('[data-cart-stock-error]'))
      ))) reportStockErrors(document);
    }).observe(document.body, { childList: true, subtree: true });
  };

  const setupModalIsolation = () => {
    const isolatedElements = new Set();
    let activeModalRoot = null;
    let activeTrigger = null;
    let pendingTrigger = null;

    const restoreBackground = () => {
      const trigger = activeTrigger;
      isolatedElements.forEach(element => {
        element.inert = false;
      });
      isolatedElements.clear();
      activeModalRoot = null;
      activeTrigger = null;
      if (trigger?.isConnected) queueMicrotask(() => trigger.focus({ preventScroll: true }));
    };

    const isolateBackground = modalRoot => {
      let current = modalRoot;
      while (current?.parentElement && current.parentElement !== document.documentElement) {
        [...current.parentElement.children].forEach(sibling => {
          if (sibling === current || sibling.inert) return;
          sibling.inert = true;
          isolatedElements.add(sibling);
        });
        if (current.parentElement === document.body) break;
        current = current.parentElement;
      }
      activeModalRoot = modalRoot;
      activeTrigger = pendingTrigger;
      pendingTrigger = null;
    };

    const findOpenModal = () => {
      const quickCart = document.querySelector('quick-cart-drawer.is--open');
      if (quickCart) return quickCart;
      const cart = document.querySelector('cart-drawer.is-visible');
      if (cart) return cart;
      return [...document.querySelectorAll('search-drawer details[open]')]
        .find(details => details.querySelector('input[type="search"]')) || null;
    };

    const sync = () => {
      const nextModal = findOpenModal();
      if (nextModal === activeModalRoot) return;
      restoreBackground();
      if (nextModal) isolateBackground(nextModal);
    };

    document.addEventListener('click', event => {
      const trigger = event.target.closest(
        'summary[id^="Search-drawer-button-"], [data-cart-link][aria-haspopup="dialog"], [aria-controls="quick-cart-drawer"]'
      );
      if (trigger) pendingTrigger = trigger;
    }, true);
    document.addEventListener('toggle', sync, true);
    new MutationObserver(sync).observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'open']
    });
    sync();
  };

  const setupMeasurement = () => {
    document.addEventListener('click', event => {
      const searchOpen = event.target.closest('summary[id^="Search-drawer-button-"]');
      if (searchOpen) emit('search_open');

      const predictiveLink = event.target.closest('predictive-search a[href]');
      if (predictiveLink) {
        const links = [...predictiveLink.closest('predictive-search').querySelectorAll('a[href]')];
        emit('predictive_result_select', {
          result_position: links.indexOf(predictiveLink) + 1,
          destination: safePath(predictiveLink.href)
        });
      }

      const productLink = event.target.closest('a[href*="/products/"]');
      if (productLink && !productLink.closest('[data-main-product]')) {
        emit('select_item', {
          item_handle: safePath(productLink.href).split('/products/')[1]?.split('/')[0],
          item_list: productLink.closest('[data-merchandising-module]')?.dataset.merchandisingModule || 'storefront'
        });
      }

      const merchandising = event.target.closest('[data-merchandising-module-click]');
      if (merchandising) {
        emit('merchandising_module_click', {
          module_id: merchandising.dataset.merchandisingModuleClick,
          destination: safePath(merchandising.href)
        });
      }
    });

    document.addEventListener('submit', event => {
      const form = event.target;
      if (form.matches('form[action*="/search"], predictive-search form')) {
        const input = form.querySelector('input[type="search"]');
        emit('search_query', { query_length: input?.value.trim().length || 0 });
      }

      if (form.matches('form[data-type="add-to-cart-form"]')) {
        const id = form.querySelector('[name="id"]')?.value;
        const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
        if (id && !form.querySelector('[name="id"]')?.disabled) {
          emit('add_to_cart', { variant_id: id, quantity });
        }
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, payload => {
        const sectionId = payload?.data?.sectionId;
        const variantId = payload?.data?.variant?.id;
        if (!variantId) return;
        const productInfo = document.getElementById(`MainProduct-${sectionId}`) || document.querySelector('[data-main-product]');
        emit('select_variant', {
          product_id: productInfo?.dataset.productId,
          variant_id: variantId
        });
      });
    }

    const reportZeroResults = root => {
      const zero = root.querySelector?.('.predictive-search__message--recovery');
      if (!zero || zero.dataset.carouselMeasured) return;
      zero.dataset.carouselMeasured = 'true';
      const input = zero.closest('predictive-search')?.querySelector('input[type="search"]');
      emit('search_zero_results', { query_length: input?.value.trim().length || 0 });
    };
    reportZeroResults(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1) reportZeroResults(node.matches?.('.predictive-search__message--recovery') ? node.parentElement : node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };

  const start = () => {
    setupSizeGuides();
    setupCartGuard();
    setupModalIsolation();
    setupMeasurement();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
